import { Component, EventEmitter, OnDestroy, OnInit, contentChild } from '@angular/core';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import Utils from '../../../util';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { PeriodUnitMapper } from '../../results-util';
import {
  ResultSetConfig,
  ResultSetConfigInterface,
  ResultSetID,
  ResultSetIds
} from '../../../models/results/result-set-config.model';
import { ResultSetConfigService } from '../../results-helper/result-set-config.service';
import {
  ResultSetDropdownId
} from '../../../models/enums/results/result-set-dropdown-ids/result-set-dropdown-config-id.enum';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { AggregationMethod } from '../../results-helper/grouped-result-table/grouped-result-table.component';
import { LooseObject } from '../../../models/loose-object.model';
import { TechnicalResults } from '../../../models/results/technical/technical-results.model';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-baseline-technical',
  templateUrl: './baseline-technical.component.html',
  styleUrls: ['./baseline-technical.component.scss']
})
export class BaselineTechnicalComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  onDestroyNotifier = new EventEmitter();

  /** Result period options */
  periodCategories?: DropdownDisplayItem[];

  /** Default result period */
  periodType = 'year';
  /** Measurement system options */
  techUnitTypes?: DropdownDisplayItem[];
  /** Default measurement system */
  techUnitType = 'metric';
  /** List of results */
  resultSelectOptions: ResultSelectOption[] = [];
  /** List of result groups */
  resultSelectGroups: ResultGroup[] = [];

  /** Table data */
  tableData?;
  /** Custom headers */
  tableHeaders?: string[];
  labelHeader?: string;
  valueHeader?: string;
  unitHeader?: string;

  /** Search criteria entered by the user */
  searchCriteria: string = '';
  /** Options matching the search criteria */
  filteredSearchData: ResultSelectOption[] = [];
  /** Option groups matching the search criteria */
  filteredGroupData: ResultGroup[] = [];
  /** List of selected results */
  selectedResults: ResultSelectOption[] = [];

  /** Technical section of the results */
  technicalResults?: TechnicalResults;

  /**
   * trigger for translation pipe
   */
  trigger?: string;

  translatedResultSelectGroups: Record<string, string> = {};
  translatedResultSelectOptions: Record<string, string> = {};

  PROJECT_ID = '';
  resultConf?: ResultSetConfig;

  projectServiceReady = false;
  resultsLoaded = false;

  aggregationMethod = AggregationMethod.FIRST;

  constructor(private projectCommService: ProjectCommunicationService,
              private toastrService: ConfiguredToastrService,
              private unitService: UnitService,
              public translationService: AnessaTranslationService,
              private resultSetConfigService: ResultSetConfigService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setTranslations();
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        if (loaded) {
          this.projectServiceReady = loaded;
          this.PROJECT_ID = this.projectCommService.getProjectId() ?? '';
          this.setup();
        }
      });
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(reloaded => {
        this.resultsLoaded = reloaded;
        this.setup();
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.setTranslations();
        if (this.projectServiceReady && this.resultsLoaded) {
          this.setTableData();
        }
        this.trigger = this.translationService.getCurrentLang();
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setup() {
    if (this.projectServiceReady && this.resultsLoaded) {
      if (this.projectCommService.getProjectInfo().defaults?.unitSystem) {
        const unitSystem = this.projectCommService.getProjectInfo().defaults.unitSystem;
        this.techUnitType = unitSystem === 'imperial' ? 'imperial' : 'metric';
      } else if (this.projectCommService.getProjectDefaults().gas === '000000000000000000003302') {
        this.techUnitType = 'imperial';
      }
      this.setupResultSelect();
      this.inputsCheckAll({ checked: true });
      this.fetchResultSetConfigData();
    }
  }

  setTranslations() {
    // label is not included in the table headers array, just used for obj creation
    this.labelHeader = this.translationService.get('RESULTS.DATA.LABEL');
    this.valueHeader = this.translationService.get('RESULTS.DATA.VALUE');
    this.unitHeader = this.translationService.get('RESULTS.DATA.UNIT');
    this.periodCategories = [
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.DAY'), 'day'),
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.MONTH'), 'month'),
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.YEAR'), 'year')
    ];
    this.techUnitTypes = [
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.METRIC'), 'metric'),
      new DropdownDisplayItem(this.translationService.get('RESULTS.GENERAL.IMPERIAL'), 'imperial')
    ];
    this.tableHeaders = [
      this.valueHeader,
      this.unitHeader
    ];
    Object.keys(this.translatedResultSelectGroups).forEach(group => {
      this.translatedResultSelectGroups[group] = this.translationService.get(group);
    });
    Object.keys(this.translatedResultSelectOptions).forEach(option => {
      this.translatedResultSelectOptions[option] = this.translationService.get(option);
    });
  }

  /**
   * Setup function for the result select.
   */
  setupResultSelect(): void {
    const results = this.projectCommService.getResults();
    if (results && results.technical) {
      this.technicalResults = results.technical;
      this.setResultSelectOptions();
      this.filteredGroupData = [];
      this.filteredSearchData = [];
      this.filteredSearchData = [...this.resultSelectOptions];
      this.filteredGroupData = [...this.resultSelectGroups];
    } else {
      this.toastrService.warning(
        this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
        this.translationService.get('RESULTS.ERRORS.NO_RESULTS'));
    }
  }

  /** Creates custom options used for the result select */
  setResultSelectOptions(): void {
    if (!this.technicalResults) return;
    this.resultSelectGroups = [];
    this.resultSelectOptions = [];
    const showGHG = this.projectCommService.getProject().projectInfo.useGHG;
    for (const section of Object.keys(this.technicalResults)) {
      if (section === 'RESULTS.DATA.TECHNICAL.GHG' && !showGHG || section === 'product_rates' || section === 'fuel_rates') {
        continue;
      }
      const mySection = this.technicalResults[section as keyof TechnicalResults]
      if (!mySection) continue;
      for (const groupId of Object.keys(mySection)) {
        this.resultSelectGroups.push({
          name: groupId,
          parent: null,
          depth: 1
        });
        this.translatedResultSelectGroups[groupId] = this.translationService.get(groupId);
        const myGroup = mySection[groupId as keyof typeof mySection]
        if (!myGroup) continue;
        for (const resultId of Object.keys(myGroup)) {
          if (!myGroup[resultId]) continue;
          if (this.isLeaf(myGroup[resultId])) {
            this.resultSelectOptions.push({
              name: resultId,
              group: groupId,
              subGroup: null,
              depth: 2,
              meta: groupId + ', ' + resultId
            });
            this.translatedResultSelectOptions[resultId] = this.translationService.get(resultId);
          } else {
            this.resultSelectGroups.push({ name: resultId, parent: groupId, depth: 2 });
            this.translatedResultSelectGroups[resultId] = this.translationService.get(resultId);
            for (const subSectionResult of Object.keys(myGroup[resultId])) {
              this.resultSelectOptions.push({
                name: subSectionResult,
                group: groupId,
                subGroup: resultId,
                depth: 3,
                meta: groupId + ', ' + resultId + ', ' + subSectionResult
              });
              this.translatedResultSelectOptions[subSectionResult] = this.translationService.get(subSectionResult);
            }
          }
        }
      }
    }
  }

  /**
   *
   * @param row object
   * @returns true if row has 'RESULTS.DATA.LABEL' and 'RESULTS.DATA.VALUES'
   */
  isLeaf(row: ResultRow | any) {
    if (!row) return false;
    return ['RESULTS.DATA.LABEL', 'RESULTS.DATA.VALUES'].every(leafKey => Object.keys(row).includes(leafKey));
  }

  /**
   * Updates the selection status of the results of the specified result group
   * @param group result group clicked
   * @param event checkbox checked event
   */
  optGroupClick(group: ResultGroup, event: MatCheckboxChange): void {
    if (event.checked) {
      for (const item of this.filteredSearchData) {
        if (item.group === group.name || item.subGroup === group.name) {
          if (!this.selectedResults.includes(item)) {
            this.selectedResults.push(item);
          }
        }
      }
    } else {
      for (const item of this.filteredSearchData) {
        if (item.group === group.name || item.subGroup === group.name) {
          if (this.selectedResults.includes(item)) {
            this.selectedResults.splice(this.selectedResults.indexOf(item), 1);
          }
        }
      }
    }
    this.setTableData();
    this.selectedResults = [...this.selectedResults];
  }

  /**
   * Updates the selection status of the specified result
   * @param result result clicked
   * @param event checkbox checked event
   */
  optClick(result: ResultSelectOption): void {
    if (!this.selectedResults.includes(result)) {
      this.selectedResults.push(result);
    } else if (this.selectedResults.includes(result)) {
      this.selectedResults.splice(this.selectedResults.indexOf(result), 1);
    }
    this.setTableData();
    this.selectedResults = [...this.selectedResults];
  }

  /**
   * Checks if all options in the group are selected
   * @param group result group to check
   */
  allGroupInputsSelected(group: ResultGroup): boolean {
    for (const item of this.filteredSearchData) {
      if (item.group === group.name || item.subGroup === group.name) {
        if (!this.selectedResults.includes(item)) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Checks if at least one option in the group is selected
   * @param group result group to check
   */
  isGroupIndeterminate(group: ResultGroup): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    if (this.filteredSearchData && this.filteredSearchData.length > 0) {
      for (const item of this.filteredSearchData) {
        if (item.group === group.name || item.subGroup === group.name) {
          if (!this.selectedResults.includes(item)) {
            atLeastOneIncluded = true;
          } else {
            allIncluded = false;
          }
        }
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  /**
   * Changes status of all options to selected or unselected
   * @param $event click event
   */
  inputsCheckAll($event: { checked: boolean }): void {
    if ($event.checked) {
      if (this.filteredSearchData && this.resultSelectOptions
        && this.filteredSearchData.length === this.resultSelectOptions.length
      ) {
        this.selectedResults = this.filteredSearchData;
      } else {
        for (const item of this.filteredSearchData) {
          if (!this.selectedResults.includes(item)) {
            this.selectedResults.push(item);
          }
        }
      }
    } else {
      if (this.filteredSearchData && this.resultSelectOptions
        && this.filteredSearchData.length === this.resultSelectOptions.length
      ) {
        this.selectedResults = [];
      } else {
        for (const item of this.filteredSearchData) {
          const index = this.selectedResults.indexOf(item);
          if (index > -1) {
            this.selectedResults.splice(index, 1);
          }
        }
      }
    }
    this.setTableData();
    setTimeout(() => {
      this.selectedResults = [...this.selectedResults];
    });
  }

  /**
   * Checks if all options are selected
   */
  allInputsSelected(): boolean {
    for (const item of this.filteredSearchData) {
      if (!this.selectedResults.includes(item)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if at least one option is selected
   */
  isCheckAllIndeterminate(): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    if (this.filteredSearchData && this.filteredSearchData.length > 0) {
      for (const item of this.filteredSearchData) {
        if (!this.selectedResults.includes(item)) {
          atLeastOneIncluded = true;
        } else {
          allIncluded = false;
        }
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  /**
   * Sets the data to be dislayed
   */
  setTableData(): void {
    this.tableData = {};
    if (!this.technicalResults) return;
    if (!this.selectedResults || !this.selectedResults.length) return;

    let techResults = Utils.deepCopy(this.technicalResults);
    let tempTableData: LooseObject = {};
    for (const key of Object.keys(this.technicalResults)) {
      techResults = { ...techResults, ...this.technicalResults[key] };
    }
    for (const group of Object.keys(techResults)) {
      if (this.selectedResults.find(res => res.group === group)) {
        tempTableData[group] = {}
        const myGroup = techResults[group as keyof TechnicalResults];
        for (const result of Object.keys(myGroup)) {
          const myResult: LooseObject = myGroup[result as keyof typeof myGroup];
          if (!myResult) continue;
          if (this.selectedResults.find(res => res.group === group && res.name === result) || group === 'RESULTS.DATA.TECHNICAL.OU') {
            if (group === 'RESULTS.DATA.TECHNICAL.OU') {
              tempTableData[group][result] = {};
              for (const child of Object.keys(myResult)) {
                if (this.selectedResults.find(c => c.name === child && c.subGroup === result)) {
                  tempTableData[group][result][child] = Utils.deepCopy(myResult[child]);
                  if (child === 'RESULTS.DATA.TECHNICAL.AAEPO') continue; // don't convert annual avg electrical output
                  this.convertResultRowToTargetUnit(tempTableData[group][result][child]);
                }
              }
              continue;
            }
            tempTableData[group][result] = Utils.deepCopy(myResult);
            if (result === 'RESULTS.DATA.TECHNICAL.RNGP') {
              this.addAdditionalRngRows(tempTableData[group], tempTableData[group][result]);
            }
            let forceConversion = false;
            if (group === "RESULTS.DATA.TECHNICAL.ADDITIONAL_PRODUCTS" || group === "RESULTS.DATA.TECHNICAL.UTILS") {
              forceConversion = true;
            }
            if (result === 'RESULTS.DATA.TECHNICAL.MPHS') {
              tempTableData[group][result]['INFO_TOOLTIP'] = this.translationService.get('RESULTS.DATA.TECHNICAL.MPHS_INFO');
            }
            this.convertResultRowToTargetUnit(tempTableData[group][result], forceConversion);
            // this.convertToPerio(tempTableData[group][result]);
          }
        }
      }
    }
    this.tableData = tempTableData;
  }

  /**
   * Checks if at least one result is selected
   */
  isShowResults(): boolean {
    if (this.selectedResults.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Filters the result select options based on the search parameters
   */
  onSearchChange(): void {
    // done in an async function call to avoid the delay caused by the function being processes
    const asyncFunction = () => {
      if (!this.resultSelectOptions) {
        return;
      }
      let search = this.searchCriteria;
      if (!search) {
        this.filteredSearchData = this.resultSelectOptions.slice();
        this.filteredGroupData = this.resultSelectGroups.slice();
        return;
      } else {
        search = search.toLowerCase();
      }

      this.filteredSearchData = this.resultSelectOptions.filter(input => {
        const translatedGroup = this.translatedResultSelectGroups[input.group];
        const translatedName = this.translatedResultSelectOptions[input.name];
        return translatedGroup.toLowerCase().indexOf(search) > -1 || translatedName.toLowerCase().indexOf(search) > -1;
      });
      this.filteredGroupData = this.resultSelectGroups.filter(
        group => {
          return this.filteredSearchData.find(x => x.group === group.name || x.subGroup === group.name);
        }
      );
    };
    asyncFunction();
  }

  /**
   * Converts result data based on the select period
   * @param row technical result data row
   * @param unitToConvert
   */
  convertToPeriodAndSetDisplayName(row: ResultRow, unitToConvert: Unit | null, forceConversion = false): any {
    let skipConversion = false;
    let translatedDisplayName = '';
    if (!unitToConvert) {
      return;
    }
    const unit = new Unit(unitToConvert);

    if (forceConversion && !unit.displayName.includes('/day') && !unit.displayName.includes('%')) {
      unit.displayName = unit.displayName + '/day';
    }
    // Don't convert period unit if retainPeriod flag set
    if (!unit.displayName.includes('/day') || row['retainPeriod']
      || unit.displayName.includes('%')
      || row.label=== 'RESULTS.DATA.TECHNICAL.OLR') {
      translatedDisplayName = this.translationService.get(unit.translationId);
      skipConversion = true;
    }
    if (!skipConversion && unit.displayName.includes('/day')) {
      let unitTranslationHack = PeriodUnitMapper[unit.id];
      if (!unitTranslationHack) {
        console.log('add this to the unit hack: ' + unit.id + ' - ' + unit.displayName);
      }
      let periodTranslation = this.translationService.get('RESULTS.GENERAL.DAY');
      if (this.periodType === 'month') {
        periodTranslation = this.translationService.get('RESULTS.GENERAL.MONTH');
        row.values[0] = row.values[0] * 30;
      } else if (this.periodType === 'year') {
        periodTranslation = this.translationService.get('RESULTS.GENERAL.YEAR');
        row.values[0] = row.values[0] * 365;
      }
      if (!unitTranslationHack && forceConversion) {
        unitTranslationHack = unit.displayName.replace('/day', '/' + periodTranslation);
      }
      translatedDisplayName = this.translationService.get(unitTranslationHack, { period: periodTranslation });
    }

    if (row.label) {
      if ((row.label).includes('BEE')) {
        let beevalues = row.values;
        for (let i = 0; i < beevalues.length; i++) {
          if (beevalues[i] > 100) {
            beevalues[i] = 100;
          }
        }
        row.values = beevalues;
      }
    }

    row.unit = Unit.parseUnitDisplayName(translatedDisplayName);
    return row;
  }

  /**
   * Converts result data based on the selected measurement system
   * @param row technical result data row
   */
  convertResultRowToTargetUnit(row: ResultRow, forceConversion = false): any {
    let imperialUnit: Unit | null = null;
    let metricUnit: Unit | null = null;
    let selectedUnit: Unit | null = null;
    if (row.unit) {
      metricUnit = this.unitService.getUnitById(row.unit);
      if (this.techUnitType === 'imperial') {
        imperialUnit = this.getImperialCounterpart(row.unit);
      }
      // Don't convert period for OLR units
      if (metricUnit && Number(metricUnit.unitType) === 36) {
        row.retainPeriod = true;
      }
      if (imperialUnit) {
        selectedUnit = imperialUnit;
      } else if (metricUnit) {
        imperialUnit = null;
        selectedUnit = metricUnit;
      } else {
        row.unit = 'n/a';
      }
    }
    if (row.values) {
      if (metricUnit && imperialUnit) {
        row.values[0] = metricUnit.convertToUnit(row.values[0], imperialUnit);
      }
    }
    if (selectedUnit) {
      this.convertToPeriodAndSetDisplayName(row, selectedUnit, forceConversion);
    }
    return row;
  }

  private addAdditionalRngRows(resultSet: LooseObject, row: ResultRow) {
    const label = row.label;
    resultSet[label] = {};
    resultSet[label][label] = this.convertResultRowToTargetUnit(Utils.deepCopy(row));
    if (this.techUnitType === 'imperial') {
      if (row.values && row.values[0]) {
        const baseValue = row.values[0];
        const mmbtuUnit = this.unitService.getUnitById('000000000000000000003810');
        const ggeUnit = this.unitService.getUnitById('000000000000000000003811');
        const mmbtuValue = mmbtuUnit.convertFromBase(Utils.deepCopy(baseValue));
        const ggeValue = ggeUnit.convertFromBase(Utils.deepCopy(baseValue));
        const mmbtuResultRow = new ResultRow();
        mmbtuResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_MMBTU');
        mmbtuResultRow.values = Utils.deepCopy(row.values);
        mmbtuResultRow.values[0] = mmbtuValue;
        this.convertToPeriodAndSetDisplayName(mmbtuResultRow, mmbtuUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_MMBTU'] = mmbtuResultRow;
        const ggeResultRow = new ResultRow();
        ggeResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GGE');
        ggeResultRow.values = Utils.deepCopy(row.values);
        ggeResultRow.values[0] = ggeValue;
        this.convertToPeriodAndSetDisplayName(ggeResultRow, ggeUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_GGE'] = ggeResultRow;
      }
    } else {
      if (row.values && row.values[0]) {
        const baseValue = row.values[0];
        const gjUnit = this.unitService.getUnitById('000000000000000000003814');
        const gwhUnit = this.unitService.getUnitById('000000000000000000003815');
        const kwhUnit = this.unitService.getUnitById('000000000000000000003818');
        const sm3Unit = this.unitService.getUnitById('000000000000000000003821');
        const gjValue = gjUnit.convertFromBase(Utils.deepCopy(baseValue));
        const gwhValue = gwhUnit.convertFromBase(Utils.deepCopy(baseValue));
        const kwhValue = kwhUnit.convertFromBase(Utils.deepCopy(baseValue));
        const sm3Value = sm3Unit.convertFromBase(Utils.deepCopy(baseValue));
        // add gj row
        const gjResultRow = new ResultRow();
        gjResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GJ');
        gjResultRow.values = Utils.deepCopy(row.values);
        gjResultRow.values[0] = gjValue;
        this.convertToPeriodAndSetDisplayName(gjResultRow, gjUnit);
        // add gwh row
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_GJ'] = gjResultRow;
        const gwhResultRow = new ResultRow();
        gwhResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GWH');
        gwhResultRow.values = Utils.deepCopy(row.values);
        gwhResultRow.values[0] = gwhValue;
        this.convertToPeriodAndSetDisplayName(gwhResultRow, gwhUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_GWH'] = gwhResultRow;
        // add kwh row (metric only)
        const kwhResultRow = new ResultRow();
        kwhResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_KWH');
        kwhResultRow.values = Utils.deepCopy(row.values);
        kwhResultRow.values[0] = kwhValue;
        this.convertToPeriodAndSetDisplayName(kwhResultRow, kwhUnit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_KWH'] = kwhResultRow;
        // add Sm3 row (metric only)
        const sm3ResultRow = new ResultRow();
        sm3ResultRow.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_SM3');
        sm3ResultRow.values = Utils.deepCopy(row.values);
        sm3ResultRow.values[0] = sm3Value;
        this.convertToPeriodAndSetDisplayName(sm3ResultRow, sm3Unit);
        resultSet[label]['RESULTS.DATA.RNG_PRODUCTION_SM3'] = sm3ResultRow;
      }
    }
  }

  /**
   * Returns the metric unit counterpart in the imperial system
   * @param unitId unit id
   */
  private getImperialCounterpart(unitId: string): Unit | null {
    let imperialUnitId = '';
    switch (unitId) {
      case '000000000000000000003601': // kg VS/m3/day
        imperialUnitId = '000000000000000000003604'; // lb VS/ft3/day
        break;
      case '000000000000000000001203': // t (tonne)
        imperialUnitId = '000000000000000000001204'; // t (short ton)
        break;
      case '000000000000000000004901': // ton/day
        imperialUnitId = '000000000000000000004902'; // short ton/day
        break;
      case '000000000000000000004905': // ton/year
        imperialUnitId = '000000000000000000004906'; // short ton/year
        break;
      case '000000000000000000001802': // m3
        imperialUnitId = '000000000000000000001803'; // ft3
        break;
      case '000000000000000000003803': // m3/day
        imperialUnitId = '000000000000000000003806'; // ft3/day
        break;
      case '000000000000000000003805': // Nm3/day
        imperialUnitId = '000000000000000000003804'; // scf/day
        break;
      case '000000000000000000003812': // Nm3/year
        imperialUnitId = '000000000000000000003816'; // scf/year
        break;
      case '000000000000000000003817': // Nm3 CH4/year
        imperialUnitId = '000000000000000000003819'; // scf CH4/year
        break;
      case '000000000000000000005201': // kg CO2eq/day
        imperialUnitId = '000000000000000000005203'; // lbs CO2eq/day
        break;
      case '000000000000000000006501': // litre/day
        imperialUnitId = '000000000000000000006502'; // gal. US/day
        break;
      case '000000000000000000005801': // nm3/hour
        imperialUnitId = '000000000000000000005802'; // scfm
        break;
      case '000000000000000000003502': // kW
        imperialUnitId = '000000000000000000003504'; // MMBtu/hour
        break;
      case '000000000000000000003708': // kWh/day
      case '000000000000000000000401': // %
        return null;
      default:
        imperialUnitId = unitId;
    }
    return this.unitService.getUnitById(imperialUnitId);
  }

  fetchResultSetConfigData() {
    if (this.projectServiceReady && this.resultsLoaded) {
      this.resultSetConfigService.fetch(
        [ResultSetID.TECHNICAL],
        this.PROJECT_ID
      )
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe((res) => {
          this.setupResultSetConfigs(res);
          this.setTableData();
        });
    }
  }

  private setupResultSetConfigs(res: ResultSetIds): void {
    if (!res && !res['obj']) {
      throw Error('invalid response from result set config, unable to process');
    }
    const processedObj = Utils.restoreMapsInJson(res['obj'], 'dropdownDefaults');
    const resultSetConfInterface: ResultSetConfigInterface = processedObj[ResultSetID.TECHNICAL];
    if (resultSetConfInterface) {
      this.resultConf = new ResultSetConfig(resultSetConfInterface);
    } else {
      this.resultConf = new ResultSetConfig();
      this.resultConf.resultSetID = ResultSetID.TECHNICAL;
    }
    this.processResultSetDefaults();
  }

  processResultSetDefaults(): void {
    if (!this.projectServiceReady || !this.resultsLoaded) return;
    if (!this.resultConf || !this.resultConf.dropdownConfigEnabled) return;
    const defaults = this.resultConf.dropdownDefaults;
    if (defaults.has(ResultSetDropdownId.TECHNICAL_SELECTED_RESULTS)) {
      this.processResultConfSelectedResults(defaults.get(ResultSetDropdownId.TECHNICAL_SELECTED_RESULTS));
    }
    if (defaults.has(ResultSetDropdownId.TECH_PERIOD_TYPE)) {
      this.periodType = defaults.get(ResultSetDropdownId.TECH_PERIOD_TYPE);
    }
    if (defaults.has(ResultSetDropdownId.TECH_UNIT_TYPE)) {
      this.techUnitType = defaults.get(ResultSetDropdownId.TECH_UNIT_TYPE);
    }
  }

  private processResultConfSelectedResults(defaultResults) {
    if (!defaultResults) return;
    const tempSelectedResults = [];
    for (const result of defaultResults) {
      const foundResult = this.filteredSearchData.find(res => res['meta'] === result);
      if (foundResult) tempSelectedResults.push(foundResult);
    }
    this.selectedResults = tempSelectedResults;
  }

  onResultSetDefaultChange() {
    if (!this.resultConf) return;
    if (this.resultConf?.dropdownConfigEnabled && Utils.isMap(this.resultConf.dropdownDefaults)) {
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.TECHNICAL_SELECTED_RESULTS, this.getSelectedResultsObj());
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.TECH_PERIOD_TYPE, this.periodType);
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.TECH_UNIT_TYPE, this.techUnitType);
    }
    this.resultSetConfigService.save(this.resultConf, this.PROJECT_ID);
  }

  private getSelectedResultsObj(): { name: string }[] {
    const defaultsData = [];
    if (this.selectedResults?.length > 0) {
      for (const item of this.selectedResults) {
        // string with combo of name and group
        defaultsData.push(item.meta);
      }
    }
    return defaultsData;
  }

  setResultSetConfDisabled() {
    if (this.resultConf?.dropdownConfigEnabled && this.PROJECT_ID) {
      this.resultConf.dropdownConfigEnabled = false;
      this.resultSetConfigService.save(this.resultConf, this.PROJECT_ID);
    }
  }
}

interface ResultGroup {
  name: string,
  parent: unknown,
  subGroup?: string | null,
  meta?: string
  depth: number,
}


interface ResultSelectOption {
  name: string,
  group: string,
  subGroup: string | null,
  meta: string,
  params?: LooseObject,
  depth: number
};